import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import formatNumber from '../../../../utils/formatNumber';
import { colors } from '../../../../constants/colors';
import { DOCUMENT_TYPES_PAYMENT_SCHEDULE } from '../../../../constants';
import styles from '../deal.module.css';

// Условия выбранного банка прямо в карточке сделки. Раньше, чтобы их посмотреть,
// менеджер возвращался стрелками к выбору банка — а повторный выбор отменяет
// одобренные заявки в других банках. Смотреть условия должно быть безопасно.
// Настоящие условия банка лежат в его предложении (other_conditions). В самой заявке
// у части банков остаётся запрошенное: у Форте percent = ставка программы (10 %),
// платёж расчётный — в сделке 193990 блок показал 10 % и 169 750 ₸ вместо одобренных
// 23 % и 308 205 ₸. Поэтому берём предложение, которое выбрал менеджер.
export const chosenOffer = (appeal) => {
  const offers = appeal?.other_conditions || [];
  if (offers.length === 0) return null;
  const picked = offers.find(
    (offer) => appeal?.offerType != null && String(offer?.offerType) === String(appeal.offerType)
  );
  return picked || (offers.length === 1 ? offers[0] : null);
};

export const chosenBankRows = (appeal) => {
  if (!appeal) return [];
  const money = (value) => (value ? `${formatNumber(value)} ₸` : null);
  const offers = appeal?.other_conditions || [];
  const offer = chosenOffer(appeal);

  if (offer) {
    return [
      ['Банк:', appeal?.bank?.name],
      ['Сумма займа:', money(offer?.amount)],
      ['Первоначальный взнос:', money(offer?.initialFee || appeal?.initialFee)],
      ['Срок кредитования:', offer?.term ? `${offer.term} мес.` : null],
      ['Ставка:', offer?.rate ? `${offer.rate}%` : null],
      ['Ежемесячный платёж:', money(offer?.payment || offer?.monthlyInstalmentAmountKZ)],
      ['КАСКО:', money(offer?.cascoAmount || appeal?.cascoAmount || appeal?.kasko_amount)],
    ].filter(([, value]) => value);
  }

  // Предложений несколько, а какое выбрано — неизвестно: цифр не показываем вовсе.
  // Взнос раньше брали из заявки — это запрошенное клиентом, и у части предложений
  // он другой: в сделке 198449 Евраз одобрил три варианта, во втором взнос
  // 2 799 000 ₸ вместо запрошенных 2 000 000 ₸, а карточка показывала 2 000 000 ₸,
  // будто условие уже согласовано. Неверная цифра хуже пустого места —
  // предложения видно на вкладке «Банки», туда и ведёт ссылка ниже.
  if (offers.length > 1) {
    return [
      ['Банк:', appeal?.bank?.name],
      ['Предложение банка:', 'не выбрано'],
    ].filter(([, value]) => value);
  }

  return [
    ['Банк:', appeal?.bank?.name],
    ['Сумма займа:', money(appeal?.loan_amount)],
    ['Первоначальный взнос:', money(appeal?.initialFee)],
    ['Срок кредитования:', appeal?.period ? `${appeal.period} мес.` : null],
    ['Ставка:', appeal?.percent ? `${appeal.percent}%` : null],
    ['Ежемесячный платёж:', money(appeal?.monthlyPayment)],
    ['КАСКО:', money(appeal?.cascoAmount || appeal?.kasko_amount)],
  ].filter(([, value]) => value);
};

// График платежей выбранного банка. Лежит в документах сделки под типом 50 и
// номером банка в serialNumberId — чтобы не показать график чужого банка, если
// заявок было несколько.
export const bankPaymentSchedule = (appeal, documents) => {
  if (!appeal?.bankId) return null;
  const found = (documents || [])
    .filter(
      (doc) =>
        // Бэкенд отдаёт тип и как typeId, и вложенным type.id — смотрим на оба.
        (doc?.typeId ?? doc?.type?.id) === DOCUMENT_TYPES_PAYMENT_SCHEDULE &&
        Number(doc?.serialNumberId) === Number(appeal.bankId) &&
        doc?.url
    )
    .sort((a, b) => (b?.id || 0) - (a?.id || 0));
  return found[0] || null;
};

const ChosenBankConditions = ({ appeal, dealId, documents }) => {
  const { pathname } = useLocation();
  const rows = chosenBankRows(appeal);
  const schedule = bankPaymentSchedule(appeal, documents);
  if (rows.length === 0) return null;

  // Блок виден на всех вкладках сделки. На самой вкладке «Банки» ссылка вела бы
  // на ту же страницу — клик выглядел как «не нажимается».
  const banksPath = `/deals/deal/${dealId}/credit`;
  const onBanksTab = pathname === banksPath;

  return (
    <>
      {rows.map(([name, value]) => (
        <React.Fragment key={name}>
          <p className={styles.stepsRow__name}>{name}</p>
          <p className={styles.stepsRow__value}>{value}</p>
        </React.Fragment>
      ))}
      {schedule ? (
        <>
          <p className={styles.stepsRow__name}>График платежей:</p>
          <p className={styles.stepsRow__value}>
            <a
              href={schedule.url}
              target="_blank"
              rel="noreferrer"
              style={{ color: colors?.cameo, textDecoration: 'underline' }}
            >
              открыть
            </a>
          </p>
        </>
      ) : null}
      {dealId && !onBanksTab ? (
        <>
          <p className={styles.stepsRow__name}>Ответы всех банков:</p>
          <p className={styles.stepsRow__value}>
            <Link to={banksPath} onClick={() => window.scrollTo(0, 0)} style={{ color: colors?.cameo, textDecoration: 'underline' }}>
              вкладка «Банки»
            </Link>
          </p>
        </>
      ) : null}
    </>
  );
};

export default ChosenBankConditions;
